import { useState } from 'react'
import { type GQLError } from '@egw/clients'

const pollFn = <T>(
  apiCall: () => Promise<T>,
  validation: (_data: T) => boolean,
  interval: number,
  maxAttempts = 10,
  currentAttempt = 0,
): Promise<T> =>
  new Promise((resolve, reject) => {
    apiCall()
      .then((res) => {
        if (!validation(res) && currentAttempt < maxAttempts - 1) {
          setTimeout(() => {
            pollFn(apiCall, validation, interval, maxAttempts, currentAttempt + 1)
              .then(resolve)
              .catch((err: unknown) => {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(err)
              })
          }, interval)
        } else if (validation(res)) {
          resolve(res)
        } else {
          reject(new Error('Max attempts reached without validation passing'))
        }
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(err)
      })
  })

interface UsePollResult<TResponse> {
  start: () => void
  isLoading: boolean
  error: GQLError | Error | null
  isSuccess: boolean
  data: TResponse | null
}

export const usePoll = <T>(
  apiCall: () => Promise<T>,
  validation: (_data: T) => boolean,
  interval: number,
  maxAttempts = 10,
): UsePollResult<T> => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<GQLError | Error | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [data, setData] = useState<T | null>(null)

  if (maxAttempts <= 0) {
    setError(new Error('Max attempts cannot be less than or equal to 0'))
  }
  if (maxAttempts * interval > 5 * 60 * 1000) {
    setError(new Error('Max call duration cannot be greater than 5 minutes'))
  }
  if (interval < 100) {
    setError(new Error('Interval cannot be less than 100 milliseconds'))
  }

  const startPolling = async (): Promise<void> => {
    setIsLoading(true)
    // Set default values
    setError(null)
    setIsSuccess(false)
    setData(null)
    try {
      const res = await pollFn<T>(apiCall, validation, interval, maxAttempts)
      setData(res)
      setIsSuccess(true)
    } catch (e) {
      setError(e as GQLError)
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  const start = (): void => {
    void startPolling()
  }

  return { start, isLoading, error, isSuccess, data }
}
